"use client";

import { Bank } from "@alba-cars/common-modules";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

import { getPartneredBanks } from "@/lib/api/banks/get-partnered-banks";

type BanksContextValue = {
  banks: Bank[];
  isLoading: boolean;
  error: string | null;
  hydrate: (data: Bank[]) => void;
  refresh: () => Promise<void>;
  getBankBySlug: (slug: string) => Bank | undefined;
};

const BanksContext = createContext<BanksContextValue | null>(null);

export const BanksProvider = ({ children }: { children: React.ReactNode }) => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const hydratedRef = useRef(false);

  const hydrate = useCallback((data: Bank[]) => {
    hydratedRef.current = true;
    setBanks(data ?? []);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await getPartneredBanks();

      if (!res.success) throw new Error("Failed to refresh banks");

      const payload = res;

      if (payload?.success && Array.isArray(payload?.data?.result)) {
        setBanks(payload.data.result);
      } else {
        throw new Error(payload?.message || "Invalid response");
      }
    } catch (err: any) {
      setError(err?.message || "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getBankBySlug = useCallback(
    (slug: string) => {
      return banks.find((b) => b.slug === slug);
    },
    [banks],
  );

  const value = useMemo(
    () => ({ banks, isLoading, error, hydrate, refresh, getBankBySlug }),
    [banks, isLoading, error, hydrate, refresh, getBankBySlug],
  );

  return <BanksContext.Provider value={value}>{children}</BanksContext.Provider>;
};

export const useBanks = () => {
  const ctx = useContext(BanksContext);
  if (!ctx) throw new Error("useBanks must be used within a BanksProvider");
  return ctx;
};
